import { useListeningMicLevel } from "@/hooks/useListeningMicLevel";
import type { Room } from "livekit-client";
import { type CSSProperties, useEffect, useState } from "react";

const BAR_WEIGHTS = [0.5, 0.8, 1, 0.75, 0.45];

function useMeterMotionAllowed() {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    if (!window.matchMedia) return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setAllowed(!preference.matches && !document.hidden);
    update();
    preference.addEventListener("change", update);
    document.addEventListener("visibilitychange", update);
    return () => {
      preference.removeEventListener("change", update);
      document.removeEventListener("visibilitychange", update);
    };
  }, []);
  return allowed;
}

export function TranscriptPresence({
  state,
  label,
  room,
  isMuted = false,
  microphoneLevel,
}: {
  state: string;
  label: string;
  room?: Room | null;
  isMuted?: boolean;
  /** Optional supplied level for the disconnected development preview. */
  microphoneLevel?: number | null;
}) {
  const motionAllowed = useMeterMotionAllowed();
  const enabled = state === "listening" && !isMuted && motionAllowed;
  const measuredLevel = useListeningMicLevel(room, enabled && microphoneLevel === undefined);
  const candidate = microphoneLevel === undefined ? measuredLevel : microphoneLevel;
  const level =
    enabled && typeof candidate === "number" && Number.isFinite(candidate)
      ? Math.min(1, Math.max(0, candidate))
      : null;

  return (
    <span data-transcript-presence>
      <span
        data-presence-bars
        data-mic-reactive={level !== null ? "true" : undefined}
        data-listening-motion={level !== null && level >= 0.04 ? "true" : undefined}
        aria-hidden="true"
      >
        {BAR_WEIGHTS.map((weight, index) => (
          <i
            key={index}
            style={
              {
                "--listening-peak": 0.25 + 0.45 * weight,
                "--mic-boost":
                  level !== null && level >= 0.04 ? 0.3 * Math.sqrt(level) * weight : 0,
              } as CSSProperties
            }
          />
        ))}
      </span>
      <span>{label}</span>
    </span>
  );
}
